import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "../lib/apiClient";
import { DOC_TYPES, statusClass } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { Receipt, Stack, ClipboardText, Truck, Barricade, ShoppingCart, Users, Package, CurrencyInr, TrendUp, Wrench, Toolbox, HandCoins, FileMinus, FilePlus } from "@phosphor-icons/react";

const DOC_ICONS = {
  quotation: ClipboardText,
  sales_order: Stack,
  purchase_order: ShoppingCart,
  invoice: Receipt,
  repair_invoice: Wrench,
  delivery_challan: Truck,
  gate_pass: Barricade,
  job_work_challan: Toolbox,
  credit_note: FileMinus,
  debit_note: FilePlus,
};

const HERO_URL = "https://static.prod-images.emergentagent.com/jobs/1e4dcefe-c27b-47bb-bc47-55df206e5c80/images/cdf2ae293ddc71340144f8f97f639c194dae7ef5304e4f9bef86b3f06c74c8b6.png";

function KpiCard({ label, value, hint, icon: Icon, accent, testId }) {
  return (
    <div className="card-base card-hover p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline">{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent || "bg-brand-secondary text-brand"}`}>
          {Icon && <Icon size={16} weight="duotone" />}
        </div>
      </div>
      <div className="font-display text-3xl font-bold tracking-tight tabular text-ink">{value}</div>
      {hint && <div className="text-xs text-ink-muted mt-1">{hint}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lowStock, setLowStock] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || e.message));
    api.get("/inventory/stock-summary", { timeout: 60000 })
      .then(r => setLowStock(r.data.rows.filter(x => x.low_stock).slice(0, 6)))
      .catch((err) => console.error('Dashboard widget load failed:', err));
  }, []);

  if (err) return <div className="p-8 text-destructive" data-testid="dashboard-error">{String(err)}</div>;
  if (!data) return (
    <div className="p-8 text-ink-muted" data-testid="dashboard-loading">Loading dashboard…</div>
  );

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Snapshot of sales, documents and masters."
        testId="dashboard-header"
      />

      <div className="p-8 space-y-6">
        {/* Hero banner */}
        <div className="relative overflow-hidden rounded-lg border border-border bg-brand">
          <img src={HERO_URL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="relative p-6 sm:p-8 text-white flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">Company</div>
              <div className="font-display text-xl sm:text-2xl font-bold mt-1">RAMKISHAN AGRI INNOVATE PRIVATE LIMITED</div>
              <div className="text-white/80 text-sm mt-1">GSTIN 09AAOCR7303L1ZU · Meerut, Uttar Pradesh</div>
            </div>
            <Link to="/invoices/new" data-testid="quick-new-invoice" className="px-4 py-2 rounded-md bg-white text-brand font-semibold text-sm hover:bg-white/90 transition-colors">+ New Tax Invoice</Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <KpiCard label="Total Sales" value={fmtINR(data.total_sales)} hint="Tax + Repair invoices" icon={CurrencyInr} testId="kpi-sales" />
          <KpiCard label="Outstanding" value={fmtINR(data.outstanding_amount)} hint="Unpaid balances" icon={TrendUp} accent="bg-[#FCEFD4] text-[#8A5A19]" testId="kpi-outstanding" />
          <KpiCard label="Collected (30d)" value={fmtINR(data.collected_last_30d || 0)} hint="Payments received" icon={HandCoins} accent="bg-[#E2EFE7] text-[#1C4F32]" testId="kpi-collected" />
          <KpiCard label="Customers" value={data.customers_count} hint="Billed parties" icon={Users} testId="kpi-customers" />
        </div>

        {lowStock.length > 0 && (
          <div className="card-base p-5 border-l-4 border-l-amber-500" data-testid="low-stock-widget">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold flex items-center gap-2"><Package size={18} className="text-amber-600" /> Low Stock Alerts</div>
              <Link to="/reports/stock-summary" className="text-xs text-brand font-semibold hover:underline" data-testid="low-stock-link">View Stock Summary →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {lowStock.map((r, i) => (
                <div key={r.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm" data-testid={`low-stock-item-${i}`}>
                  <span className="font-semibold truncate mr-2">{r.name}</span>
                  <span className="text-xs whitespace-nowrap"><b className={r.closing < 0 ? "text-destructive" : "text-amber-700"}>{r.closing}</b> / min {r.min_stock} {r.unit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Document Type Grid */}
        <div>
          <div className="label-overline mb-3">Documents</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {Object.entries(DOC_TYPES).map(([key, cfg]) => {
              const Icon = DOC_ICONS[key] || Receipt;
              return (
                <Link
                  key={key}
                  to={`/${cfg.path}`}
                  data-testid={`dash-doc-${key}`}
                  className="card-base card-hover p-4 flex flex-col gap-2"
                >
                  <div className="w-8 h-8 rounded-md bg-brand-secondary text-brand flex items-center justify-center"><Icon size={16} weight="duotone" /></div>
                  <div className="font-display font-semibold text-sm text-ink">{cfg.label}</div>
                  <div className="tabular text-2xl font-bold text-ink">{data.counts[key] ?? 0}</div>
                  <div className="text-[11px] text-ink-muted">{fmtINR(data.totals[key] || 0)}</div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent docs */}
        <div className="card-base p-0 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <div className="font-display text-base font-semibold text-ink">Recent Documents</div>
              <div className="text-xs text-ink-muted">Latest 10 across all types</div>
            </div>
          </div>
          <div className="table-wrapper border-0 rounded-none">
            <table className="table-base">
              <thead><tr>
                <th>Doc No</th><th>Type</th><th>Party</th><th>Date</th><th>Status</th><th className="text-right">Amount</th>
              </tr></thead>
              <tbody>
                {data.recent_documents.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-ink-muted">No documents yet. Create your first one.</td></tr>
                )}
                {data.recent_documents.map(d => {
                  const cfg = DOC_TYPES[d.doc_type];
                  return (
                  <tr key={d.id} data-testid={`recent-doc-${d.id}`}>
                    <td>{cfg ? <Link to={`/${cfg.path}/${d.id}`} className="text-brand font-semibold hover:underline">{d.doc_number}</Link> : <span className="font-semibold text-ink">{d.doc_number}</span>}</td>
                    <td>{cfg?.label || d.doc_type}</td>
                    <td>{d.party_snapshot?.name || "—"}</td>
                    <td>{fmtDate(d.doc_date)}</td>
                    <td><span className={`status-pill ${statusClass(d.status)}`}>{d.status}</span></td>
                    <td className="text-right font-semibold">{fmtINR(d.grand_total)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
